import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PageLayout } from '../components/Layout';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';
import { ThumbsUp, ThumbsDown, Meh, Loader2, CheckCircle2, Star, PenLine } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function StarRating({ rating, onRate, size = 'w-8 h-8' }) {
  const [hovered, setHovered] = useState(0);

  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onMouseEnter={() => setHovered(star)}
          onMouseLeave={() => setHovered(0)}
          onClick={() => onRate(star)}
          className="transition-transform hover:scale-110"
          data-testid={`review-star-${star}`}
        >
          <Star
            className={`${size} transition-colors ${
              star <= (hovered || rating)
                ? 'fill-amber-400 text-amber-400'
                : 'fill-none text-gray-300'
            }`}
          />
        </button>
      ))}
    </div>
  );
}

export default function FeedbackPage() {
  const navigate = useNavigate();
  const [rating, setRating] = useState(null);
  const [feedbackText, setFeedbackText] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Review state
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewText, setReviewText] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [career, setCareer] = useState('');
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewSubmitted, setReviewSubmitted] = useState(false);
  const [existingReview, setExistingReview] = useState(null);

  useEffect(() => {
    fetchExistingReview();
  }, []);

  const fetchExistingReview = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/reviews/mine`);
      if (data) {
        setExistingReview(data);
        setReviewRating(data.rating);
        setReviewText(data.review_text);
        setDisplayName(data.display_name || '');
        setCareer(data.career || '');
      }
    } catch {
      // Not logged in or no review - ignore
    }
  };

  const handleRatingSelect = async (selectedRating) => {
    setRating(selectedRating);
    if (selectedRating === 'positive') {
      await submitFeedback(selectedRating, null);
    }
  };

  const submitFeedback = async (ratingToSubmit, text) => {
    setLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/feedback`,
        { rating: ratingToSubmit, feedback_text: text },
        
      );
      setSubmitted(true);
      toast.success('Thank you for your feedback!');
    } catch {
      toast.error('Failed to submit feedback');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitWithText = async () => {
    await submitFeedback(rating, feedbackText);
  };

  const handleSubmitReview = async () => {
    if (reviewRating === 0) {
      toast.error('Please select a star rating');
      return;
    }
    if (reviewText.trim().length < 10) {
      toast.error('Please write at least 10 characters');
      return;
    }

    setReviewLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/reviews`,
        {
          rating: reviewRating,
          review_text: reviewText.trim(),
          display_name: displayName.trim() || null,
          career: career.trim() || null
        },
        
      );
      setReviewSubmitted(true);
      toast.success(existingReview ? 'Review updated!' : 'Review submitted!');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to submit review';
      toast.error(typeof msg === 'string' ? msg : 'Failed to submit review');
    } finally {
      setReviewLoading(false);
    }
  };

  if (submitted && reviewSubmitted) {
    return (
      <PageLayout showNav>
        <div className="px-4 py-12 max-w-lg mx-auto text-center">
          <div className="w-20 h-20 bg-primary rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-white" />
          </div>
          <h1 className="font-heading font-bold text-dark text-2xl mb-4">
            Thank You!
          </h1>
          <p className="text-text-secondary mb-8">
            Your feedback and review help us improve My Direct Debits for everyone.
          </p>
          <Button
            onClick={() => navigate('/dashboard')}
            className="btn-primary px-8 py-3"
          >
            Back to Dashboard
          </Button>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout showNav>
      <div className="px-4 py-6 max-w-lg mx-auto space-y-6">
        <div>
          <Link to="/settings" className="text-primary text-sm hover:underline">
            ← Back to Settings
          </Link>
          <h1 className="font-heading font-bold text-dark text-2xl mt-2">
            Send Feedback
          </h1>
          <p className="text-text-secondary mt-1">
            How are you finding My Direct Debits?
          </p>
        </div>

        {/* Rating Selection */}
        {!submitted ? (
          <>
            <div className="card-default p-6">
              <p className="text-center text-text-secondary mb-6">
                Select how you feel about the app
              </p>
              <div className="flex justify-center gap-6">
                <button
                  onClick={() => handleRatingSelect('positive')}
                  disabled={loading}
                  data-testid="rating-positive"
                  className={`p-4 rounded-2xl transition-all ${
                    rating === 'positive'
                      ? 'bg-primary text-white scale-110'
                      : 'bg-primary-light text-primary hover:scale-105'
                  }`}
                >
                  <ThumbsUp className="w-8 h-8" />
                  <span className="text-xs mt-1 block">Loving it</span>
                </button>
                
                <button
                  onClick={() => handleRatingSelect('neutral')}
                  disabled={loading}
                  data-testid="rating-neutral"
                  className={`p-4 rounded-2xl transition-all ${
                    rating === 'neutral'
                      ? 'bg-gold text-white scale-110'
                      : 'bg-gold-light text-gold hover:scale-105'
                  }`}
                >
                  <Meh className="w-8 h-8" />
                  <span className="text-xs mt-1 block">It's okay</span>
                </button>
                
                <button
                  onClick={() => handleRatingSelect('negative')}
                  disabled={loading}
                  data-testid="rating-negative"
                  className={`p-4 rounded-2xl transition-all ${
                    rating === 'negative'
                      ? 'bg-alert text-white scale-110'
                      : 'bg-alert-light text-alert hover:scale-105'
                  }`}
                >
                  <ThumbsDown className="w-8 h-8" />
                  <span className="text-xs mt-1 block">Needs work</span>
                </button>
              </div>
            </div>

            {/* Text Feedback (for neutral/negative) */}
            {(rating === 'neutral' || rating === 'negative') && (
              <div className="card-default p-6 animate-fade-in">
                <p className="font-medium text-dark mb-4">
                  {rating === 'neutral' 
                    ? "What could make it better?" 
                    : "We're sorry to hear that. What can we improve?"}
                </p>
                <Textarea
                  value={feedbackText}
                  onChange={(e) => setFeedbackText(e.target.value)}
                  placeholder="Tell us your thoughts..."
                  data-testid="feedback-text"
                  className="input-field w-full min-h-[120px] mb-4"
                />
                <Button
                  onClick={handleSubmitWithText}
                  disabled={loading || !feedbackText.trim()}
                  data-testid="submit-feedback-btn"
                  className="btn-primary w-full py-3"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                  ) : (
                    'Submit Feedback'
                  )}
                </Button>
              </div>
            )}

            {loading && rating === 'positive' && (
              <div className="text-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto" />
              </div>
            )}
          </>
        ) : (
          <div className="card-default p-6 text-center">
            <CheckCircle2 className="w-10 h-10 text-primary mx-auto mb-3" />
            <p className="font-medium text-dark">Feedback sent!</p>
            <p className="text-text-secondary text-sm mt-1">Thank you for sharing how you feel.</p>
          </div>
        )}

        {/* Leave a Review Section */}
        {!reviewSubmitted ? (
          <div className="card-default p-6" data-testid="review-section">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 bg-amber-50 rounded-full flex items-center justify-center flex-shrink-0">
                <PenLine className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h2 className="font-heading font-semibold text-dark text-lg">
                  Leave a Review
                </h2>
                <p className="text-text-light text-sm">
                  {existingReview ? 'Update your public review' : 'Share your experience with others'}
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Your Rating
                </label>
                <StarRating rating={reviewRating} onRate={setReviewRating} />
              </div>

              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Your Review
                </label>
                <Textarea
                  value={reviewText}
                  onChange={(e) => setReviewText(e.target.value)}
                  placeholder="Tell others about your experience with My Direct Debits..."
                  data-testid="review-text"
                  className="input-field w-full min-h-[100px]"
                  maxLength={1000}
                />
                <p className="text-text-light text-xs mt-1 text-right">{reviewText.length}/1000</p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Display Name
                  </label>
                  <Input
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder="Your name"
                    data-testid="review-display-name"
                    className="input-field w-full"
                    maxLength={50}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Career / Role
                  </label>
                  <Input
                    value={career}
                    onChange={(e) => setCareer(e.target.value)}
                    placeholder="e.g. Teacher"
                    data-testid="review-career"
                    className="input-field w-full"
                    maxLength={50}
                  />
                </div>
              </div>

              <Button
                onClick={handleSubmitReview}
                disabled={reviewLoading || reviewRating === 0 || reviewText.trim().length < 10}
                data-testid="submit-review-btn"
                className="btn-primary w-full py-3"
              >
                {reviewLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                ) : existingReview ? (
                  'Update Review'
                ) : (
                  'Submit Review'
                )}
              </Button>
            </div>
          </div>
        ) : (
          <div className="card-default p-6 text-center" data-testid="review-submitted">
            <CheckCircle2 className="w-10 h-10 text-amber-500 mx-auto mb-3" />
            <p className="font-medium text-dark">Review {existingReview ? 'updated' : 'submitted'}!</p>
            <p className="text-text-secondary text-sm mt-1">Your review will appear on our home page.</p>
          </div>
        )}
      </div>
    </PageLayout>
  );
}
